import * as React from 'react'
import * as Router from 'react-router-dom'

import { PostAnchors, PostContent, PostEditors, PostFootNotes, PostImage } from './content'

export interface PostMeta<T extends string> {
  type: T
  id: number
  url: string
  title: string
  revision: string
  revision_url: string
  publish_time: string
}

export type Revision = PostContent & PostAnchors & PostFootNotes & PostMeta<'revision'>
export type Post = PostContent & PostAnchors & PostFootNotes & PostEditors & PostMeta<'post'>
export type Draft = PostContent & PostAnchors & PostFootNotes & PostEditors & PostMeta<'draft'>

const postPrefix = 'https://chahoutan.teacon.cn/v1/posts'
const imagePrefix = 'https://chahoutan.teacon.cn/v1/images'

export function createPost(): PostContent & PostEditors {
  return {
    text: '', anchors: [], images: [], editors: [], footnotes: {}
  }
}

export function toCSTDateString(id: number) {
  const offset = 28_800_000, timestamp = 1_611_920_400_000 + 201_600_000 * id
  const isoTimeString = new Date(offset + timestamp).toISOString()
  return isoTimeString.substring(0, isoTimeString.indexOf('T'))
}

function getAuthorizationHeader() {
  const token = localStorage.getItem('chahoutan_token')
  return token ? { 'Authorization': 'Bearer ' + token } : {} as HeadersInit
}

function getBlobFromJson<T>(json: T) {
  return new Blob([JSON.stringify(json)], {type: 'application/json'})
}

async function fetchUrl<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    method: 'GET', mode: 'cors',
    headers: {
      ...getAuthorizationHeader()
    }
  })
  if (response.ok) {
    return await response.json()
  }
  throw new Error()
}

async function fetchDelete<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    method: 'DELETE', mode: 'cors',
    headers: {
      ...getAuthorizationHeader()
    }
  })
  if (response.ok) {
    return await response.json()
  }
  throw new Error()
}

async function fetchBlob<T>(url: string, body: Blob): Promise<T> {
  const response = await fetch(url, {
    method: 'POST', mode: 'cors', body,
    headers: {
      'Content-Type': body.type,
      ...getAuthorizationHeader()
    }
  })
  if (response.ok) {
    return await response.json()
  }
  throw new Error()
}

async function fetchPosts(minDelay: number): Promise<Post[]> {
  const delayPromise = new Promise(resolve => setTimeout(resolve, minDelay))
  const fetchPromise = fetchUrl<Post[]>(postPrefix)
  return await Promise.all([fetchPromise, delayPromise]).then(([posts, _]) => posts)
}

async function fetchPostsUntil(until: number, minDelay: number): Promise<Post[]> {
  const delayPromise = new Promise(resolve => setTimeout(resolve, minDelay))
  const fetchPromise = fetchUrl<Post[]>(postPrefix + `?${new URLSearchParams([['until', '' + until]])}`)
  return await Promise.all([fetchPromise, delayPromise]).then(([posts, _]) => posts)
}

async function fetchPost(id: number, minDelay: number): Promise<(Post | Revision | Draft)[]> {
  if (!localStorage.getItem('chahoutan_token')) {
    const delayPromise = new Promise(resolve => setTimeout(resolve, minDelay))
    const fetchPromise = fetchUrl<Post>(postPrefix + '/' + id).then(post => [post])
    return await Promise.all([fetchPromise, delayPromise]).then(([posts, _]) => posts)
  } else {
    const delayPromise = new Promise(resolve => setTimeout(resolve, minDelay))
    const fetchPromise = fetchUrl<(Revision | Draft)[]>(`${postPrefix}/${id}/revisions`)
    return await Promise.all([fetchPromise, delayPromise]).then(([revisions, _]) => revisions)
  }
}

async function searchPosts(search: string): Promise<Post[]> {
  return await fetchUrl(postPrefix + `?${new URLSearchParams([['q', search]])}`)
}

async function uploadPost(id: number, post: PostContent & PostEditors): Promise<PostImage> {
  return await fetchBlob(postPrefix, getBlobFromJson({ ...post, id }))
}

async function uploadImage(image: Blob): Promise<PostImage> {
  return await fetchBlob(imagePrefix, image)
}

async function removeImage(id: string): Promise<void> {
  return await fetchDelete(imagePrefix + '/' + id)
}

export function useEditPost(id: number) {
  const [postId, setPostId] = React.useState<number | null>(id)

  const history = Router.useHistory()

  React.useEffect(() => {
    if (!localStorage.getItem('chahoutan_token')) {
      history.push('/')
    }
  })

  const submitId = React.useCallback((newId: number | null) => {
    if (newId !== null) {
      history.replace('/' + newId + '/edit')
    }
    setPostId(newId)
  }, [setPostId])

  const submitPost = React.useCallback(async (newPost: PostContent & PostEditors) => {
    await uploadPost(id, newPost)
    history.push('/' + id)
  }, [id])

  return [postId, { submitId, submitPost, uploadImage, removeImage }] as const
}

export function usePosts() {
  const initState: { until: number, posts: Post[] } = { until: -1, posts: [] }

  const [state, setState] = React.useState(initState)

  const [nextPost, setNextPost] = React.useState(0)

  const wait = React.useCallback(() => setState(initState), [])

  const refresh = React.useCallback(async () => {
    setState({ until: -1, posts: state.posts })
    const posts = [...state.posts, ...await fetchPosts(150)]
    setState({ until: (posts[posts.length - 1]?.id ?? 1) - 1, posts })
    setNextPost(posts.length > 0 ? posts[0].id + 1 : nextPost)
  }, [state.posts])

  const append = React.useCallback(async () => {
    const oldUntil = state.until
    setState({ until: -1, posts: state.posts })
    const posts = [...state.posts, ...await fetchPostsUntil(oldUntil, 150)]
    setState({ until: (posts[posts.length - 1]?.id ?? 1) - 1, posts })
  }, [state.until, state.posts])

  const query = React.useCallback(async (search: string) => {
    const filteredPosts = await searchPosts(search)
    setState({ until: 0, posts: filteredPosts })
  }, [])

  React.useEffect(() => (refresh(), undefined), [])

  return [nextPost, state.until, state.posts, { wait, refresh, append, query }] as const
}

export function usePost(id: number) {
  const [revisions, setRevisions] = React.useState<(Post | Revision | Draft)[] | null>(null)

  const refresh = React.useCallback(async () => {
    const posts = await fetchPost(id, 150).catch(() => [])
    setRevisions(posts)
  }, [id])

  React.useEffect(() => (refresh(), undefined), [id])

  return [revisions, { refresh }] as const
}
